import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Loader2, CheckCircle, AlertTriangle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { usePerms } from '../permissions/PermissionsContext';
import { useLanguage } from '../context/LanguageContext';
import BreadcrumbNav, { BREADCRUMB_ITEMS } from '../components/BreadcrumbNav';
import DashboardHeader from '../components/DashboardHeader';
import AllAdminsSection from './account-settings/sections/AllAdminsSection';
import SupervisorRecordsSection from './account-settings/sections/SupervisorRecordsSection';
import PendingAdminsSection from './account-settings/sections/PendingAdminsSection';
import AddAdminIdSection from './account-settings/sections/AddAdminIdSection';
import CreateAdminFromSupervisorSection from './account-settings/sections/CreateAdminFromSupervisorSection';
import PermissionsSection from './account-settings/sections/PermissionsSection';
import CompanyDocumentsSection from './account-settings/sections/CompanyDocumentsSection';
import CompanySettingsSection from './account-settings/sections/CompanySettingsSection';
import BankAccountsSection from './account-settings/sections/BankAccountsSection';
import ExpiryWatchSection from './account-settings/sections/ExpiryWatchSection';
import SecuritySection from './account-settings/sections/SecuritySection';
import AdminHubSection from './account-settings/sections/AdminHubSection';
import ProfileSection from './account-settings/sections/ProfileSection';
import EmailDiagnosticsSection from './account-settings/sections/EmailDiagnosticsSection';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Logo
const LOGO_TRIANGLE = '/brand/ast-logo-asb606.png';

export default function AccountSettings() {
  const { user, updateUser, getAuthHeaders, logout } = useAuth();
  const { language } = useLanguage();
  const [searchParams] = useSearchParams();
  const [profile, setProfile] = useState({ name: '', email: '', phone: '', username: '', fullName: '' });
  const [passwords, setPasswords] = useState({ current: '', new: '', confirm: '' });
  const [showPasswords, setShowPasswords] = useState({ current: false, new: false, confirm: false });
  const [saving, setSaving] = useState(false);
  const [savingPassword, setSavingPassword] = useState(false);
  const [activeSection, setActiveSection] = useState('main');
  const [allAdmins, setAllAdmins] = useState([]);
  const companyDocsBackRef = useRef(null);
  const [loadingAdmins, setLoadingAdmins] = useState(false);
  const [creatingAdmin, setCreatingAdmin] = useState(false);
  const [newAdmin, setNewAdmin] = useState({
    username: '',
    email: '',
    phone: '',
    first_name: '',
    last_name: '',
    employee_id: '',
    job_title_ar: '',
    preferred_language: 'ar'
  });
  const [successDialog, setSuccessDialog] = useState(null); // { username, email } shown after create
  const [resetLinkModal, setResetLinkModal] = useState(null); // { admin, language, sending } for send-reset modal
  const [emailSentInfo, setEmailSentInfo] = useState(null); // لعرض معلومات البريد المرسل
  const [pendingAdmins, setPendingAdmins] = useState([]); // المشرفين المعلقين
  const [loadingPending, setLoadingPending] = useState(false);
  const [resendingEmail, setResendingEmail] = useState(null); // لتتبع إعادة الإرسال
  const [regeneratingUrl, setRegeneratingUrl] = useState(false); // إعادة تشفير الرابط
  const [showRegenerateModal, setShowRegenerateModal] = useState(false); // Modal تأكيد إعادة التشفير
  const [newSecureUrl, setNewSecureUrl] = useState(null); // الرابط الجديد بعد إعادة التشفير
  
  // Emirates-ID form state moved to AddAdminIdSection.jsx
  const [jobTitles, setJobTitles] = useState([]);
  const [loadingJobTitles, setLoadingJobTitles] = useState(false);

  
  // Registered Supervisors (without user accounts)
  const [registeredSupervisors, setRegisteredSupervisors] = useState([]);
  const [loadingSupervisors, setLoadingSupervisors] = useState(false);
  const [supervisorRecords, setSupervisorRecords] = useState([]);
  const [loadingSupervisorRecords, setLoadingSupervisorRecords] = useState(false);
  const [supervisorRecordsSearch, setSupervisorRecordsSearch] = useState('');
  const [selectedSupervisor, setSelectedSupervisor] = useState(null);
  
  const navigate = useNavigate();


  const headers = getAuthHeaders();

  const fetchAllAdmins = useCallback(async () => {
    setLoadingAdmins(true);
    try {
      const res = await axios.get(`${BACKEND_URL}/api/admin/all-admins`, { headers });
      setAllAdmins(res.data.data || []);
    } catch (err) {
      console.log('Error fetching admins:', err);
      toast.error('فشل في جلب بيانات الأدمين');
    } finally {
      setLoadingAdmins(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchJobTitles = useCallback(async () => {
    setLoadingJobTitles(true);
    try {
      const res = await axios.get(`${BACKEND_URL}/api/dropdowns/job-titles?active_only=true`, { headers });
      setJobTitles(res.data.data || []);
    } catch (err) {
      console.log('Error fetching job titles:', err);
    } finally {
      setLoadingJobTitles(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Fetch registered supervisors (added via ID but no user account yet)
  const fetchRegisteredSupervisors = useCallback(async () => {
    setLoadingSupervisors(true);
    try {
      const res = await axios.get(`${BACKEND_URL}/api/admin/registered-supervisors`, { headers });
      setRegisteredSupervisors(res.data.data || []);
    } catch (err) {
      console.log('Error fetching registered supervisors:', err);
      // Fallback: filter from all admins those without activated accounts
      setRegisteredSupervisors([]);
    } finally {
      setLoadingSupervisors(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Fetch ALL supervisor records (form entries, regardless of user account state)
  const fetchSupervisorRecords = useCallback(async () => {
    setLoadingSupervisorRecords(true);
    try {
      const res = await axios.get(`${BACKEND_URL}/api/admin/supervisor-records`, { headers });
      setSupervisorRecords(res.data.data || []);
    } catch (err) {
      console.log('Error fetching supervisor records:', err);
      setSupervisorRecords([]);
    } finally {
      setLoadingSupervisorRecords(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Archive a supervisor record (soft-delete; moves it to /control/archive/asb606-admin/supervisors)
  const archiveSupervisor = useCallback(async (rec) => {
    const name = rec.full_name_ar
      || `${rec.first_name || ''} ${rec.last_name || ''}`.trim()
      || rec.email
      || '';
    const confirmText = language === 'ar'
      ? `أرشفة سجلّ "${name}"؟ سيتم نقله للأرشيف ويمكن استعادته أو حذفه نهائياً من هناك.`
      : `Archive record "${name}"? It will be moved to the archive.`;
    if (!window.confirm(confirmText)) return;
    try {
      await axios.post(
        `${BACKEND_URL}/api/admin/archive/supervisors/${rec.id}`,
        {},
        { headers }
      );
      toast.success(language === 'ar' ? 'تم أرشفة السجلّ' : 'Record archived');
      fetchSupervisorRecords();
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (err.response?.status === 409 && detail && typeof detail === 'object' && detail.code === 'active_user_linked') {
        toast.error(
          (language === 'ar'
            ? `لا يمكن أرشفة هذا السجل: لديه حساب يوزر فعال (${detail.linked_username || ''}). الرجاء إلغاء/أرشفة حساب اليوزر أولاً من قسم "كل الأدمن".`
            : `Cannot archive: this profile has an active user account (${detail.linked_username || ''}). Please revoke/archive the user account first from the "Admins" tab.`),
          { duration: 7000 },
        );
        return;
      }
      const msg = typeof detail === 'string' ? detail : (detail?.message || (language === 'ar' ? 'فشل الأرشفة' : 'Archive failed'));
      toast.error(msg);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [language, fetchSupervisorRecords]);

  // ----- Admin row actions: send reset link / ban / delete -----
  const sendAdminResetLink = useCallback((admin) => {
    // Open the modal — user picks the language (pre-selected from user.preferred_language)
    setResetLinkModal({
      admin,
      language: (admin.preferred_language || 'ar').toLowerCase() === 'en' ? 'en' : 'ar',
      sending: false,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const confirmSendResetLink = useCallback(async () => {
    if (!resetLinkModal?.admin) return;
    setResetLinkModal((m) => ({ ...m, sending: true }));
    try {
      await axios.post(
        `${BACKEND_URL}/api/admin/users/${resetLinkModal.admin.id}/send-reset-link`,
        { language: resetLinkModal.language },
        { headers }
      );
      toast.success(language === 'ar' ? 'تم إرسال رابط إعادة التعيين' : 'Reset link sent');
      setResetLinkModal(null);
    } catch (err) {
      const detail = err.response?.data?.detail;
      const msg = typeof detail === 'string'
        ? detail
        : (detail?.message || (language === 'ar' ? 'فشل إرسال الرابط' : 'Failed to send link'));
      toast.error(msg, { duration: 6000 });
      setResetLinkModal((m) => ({ ...m, sending: false }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resetLinkModal, language, headers]);

  const toggleBanAdmin = useCallback(async (admin) => {
    const isCurrentlyBanned = admin.status === 'banned';
    const action = isCurrentlyBanned ? (language === 'ar' ? 'إلغاء حظر' : 'unban') : (language === 'ar' ? 'حظر' : 'ban');
    const confirmText = language === 'ar'
      ? `${action} الحساب "${admin.name || admin.email}"؟`
      : `${action.charAt(0).toUpperCase() + action.slice(1)} account "${admin.name || admin.email}"?`;
    if (!window.confirm(confirmText)) return;
    try {
      await axios.put(
        `${BACKEND_URL}/api/admin/users/${admin.id}/ban`,
        { banned: !isCurrentlyBanned, reason: isCurrentlyBanned ? '' : 'Admin action' },
        { headers }
      );
      toast.success(language === 'ar'
        ? (isCurrentlyBanned ? 'تم إلغاء الحظر' : 'تم حظر الحساب')
        : (isCurrentlyBanned ? 'Account unbanned' : 'Account banned'));
      fetchAllAdmins();
    } catch (err) {
      toast.error(err.response?.data?.detail || (language === 'ar' ? 'فشل تنفيذ الإجراء' : 'Action failed'));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [language, fetchAllAdmins]);

  const archiveAdmin = useCallback(async (admin) => {
    const confirmText = language === 'ar'
      ? `أرشفة الحساب "${admin.name || admin.email}"؟ سيتم نقله للأرشيف ويمكن استعادته أو حذفه نهائياً من هناك.`
      : `Archive account "${admin.name || admin.email}"? It will be moved to the archive where you can restore or permanently delete it.`;
    if (!window.confirm(confirmText)) return;
    try {
      await axios.post(
        `${BACKEND_URL}/api/admin/archive/admins/${admin.id}`,
        {},
        { headers }
      );
      toast.success(language === 'ar' ? 'تم أرشفة الحساب' : 'Account archived');
      fetchAllAdmins();
    } catch (err) {
      toast.error(err.response?.data?.detail || (language === 'ar' ? 'فشل الأرشفة' : 'Archive failed'));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [language, fetchAllAdmins]);

  // الصلاحيات: كل تبويب هنا = صفحة فرعية تحت ASB606 ADMIN في سجل الصلاحيات
  const perms = usePerms();
  const SECTION_PAGE = {
    admins: '/control/asb606-admin/admins', 'all-admins': '/control/asb606-admin/admins', 'create-admin': '/control/asb606-admin/admins',
    'add-admin-id': '/control/asb606-admin/admins', 'pending-admins': '/control/asb606-admin/admins', 'supervisor-records': '/control/asb606-admin/admins',
    permissions: '/control/asb606-admin/permissions', security: '/control/asb606-admin/security', 'company-docs': '/control/asb606-admin/company-docs',
    'partner-code': '/control/asb606-admin/partner-code', 'bank-account': '/control/asb606-admin/bank-account', 'expiry-watch': '/control/asb606-admin/expiry-watch',
  };
  const governedPage = SECTION_PAGE[activeSection];
  const sectionBlocked = governedPage && !perms.canView(governedPage);
  useEffect(() => {
    if (!sectionBlocked) return;
    toast.error(language === 'ar' ? 'ليس لديك صلاحية الدخول إلى هذه الصفحة' : 'You do not have permission to open this page');
    navigate('/dashboard', { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sectionBlocked]);

  // Handle URL params for direct navigation (e.g., ?tab=security or ?section=company-docs)
  useEffect(() => {
    // Support both ?tab= and ?section= as aliases (some breadcrumbs/tests use either)
    const tab = searchParams.get('tab') || searchParams.get('section');
    if (tab) {
      setActiveSection(tab);
      if (tab === 'permissions' || tab === 'admins' || tab === 'all-admins') {
        fetchAllAdmins();
      }
      if (tab === 'admins') {
        fetchJobTitles();
      }
    } else if (perms.canView('/control/asb606-admin/admins')) {
      // Default to admins section if no tab specified
      setActiveSection('admins');
      fetchJobTitles();
    } else {
      setActiveSection('profile'); // staff without admin-management permission land on their own profile
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, fetchAllAdmins, fetchJobTitles]);

  // Handler invoked when entering create-admin section
  const initCreateAdminSection = useCallback(() => {
    fetchRegisteredSupervisors();
    setSelectedSupervisor(null);
    setNewAdmin({
      username: '',
      email: '',
      phone: '',
      first_name: '',
      last_name: '',
      employee_id: '',
      job_title_ar: '',
      preferred_language: 'ar'
    });
    setEmailSentInfo(null);
    setSuccessDialog(null);
  }, [fetchRegisteredSupervisors]);

  // Load job titles when entering add-admin-id section
  useEffect(() => {
    if (activeSection === 'add-admin-id') {
      fetchJobTitles();
    }
    if (activeSection === 'create-admin') {
      initCreateAdminSection();
      fetchJobTitles();
    }
    if (activeSection === 'pending-admins') {
      fetchPendingAdmins();
    }
    if (activeSection === 'supervisor-records') {
      fetchSupervisorRecords();
    }
    if (activeSection === 'admins' || activeSection === 'all-admins' || activeSection === 'permissions') {
      fetchAllAdmins();
    }
    if (activeSection === 'all-admins') {
      fetchJobTitles();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeSection]);

  // Scroll to top on section change
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [activeSection]);

  useEffect(() => {
    if (user) {
      setProfile({
        name: user.name || '',
        email: user.email || '',
        phone: user.phone || '',
        username: user.username || (user.email ? user.email.split('@')[0] : ''),
        fullName: user.full_name || user.name || ''
      });
    } else {
      navigate('/');
    }
  }, [user, navigate]);

  // Generate username from name
  const generateUsername = (firstName, lastName) => {
    const base = `${firstName}.${lastName}`.toLowerCase().replace(/\s+/g, '');
    const random = Math.floor(Math.random() * 999) + 1;
    return `${base}${random}`;
  };

  // Handle supervisor selection. Pass null to clear.
  const handleSupervisorSelect = (supervisorId) => {
    if (!supervisorId) {
      setSelectedSupervisor(null);
      return;
    }
    const supervisor = registeredSupervisors.find(s => s.id === supervisorId);
    if (supervisor) {
      setSelectedSupervisor(supervisor);
      // Username = employee_id by default (admin can manually edit later).
      // Fall back to generated name only when the supervisor has no employee_id.
      const defaultUsername = supervisor.employee_id
        ? String(supervisor.employee_id).trim()
        : generateUsername(supervisor.first_name || '', supervisor.last_name || '');
      setNewAdmin({
        username: defaultUsername,
        email: supervisor.email || '',
        phone: supervisor.phone || '',
        first_name: supervisor.first_name || '',
        last_name: supervisor.last_name || '',
        employee_id: supervisor.employee_id || '',
        job_title_ar: supervisor.job_title_ar || supervisor.job_title || '',
        job_title_en: supervisor.job_title_en || supervisor.job_title || '',
        job_title_id: supervisor.job_title_id || '',
        preferred_language: supervisor.preferred_language || 'ar'
      });
    }
  };


  // Emirates-ID form handlers moved to AddAdminIdSection.jsx


  const fetchPendingAdmins = async () => {
    setLoadingPending(true);
    try {
      const res = await axios.get(`${BACKEND_URL}/api/admin/pending-admins`, { headers });
      setPendingAdmins(res.data.data || []);
    } catch (err) {
      console.log('Error fetching pending admins:', err);
      toast.error('فشل في جلب بيانات المشرفين المعلقين');
    } finally {
      setLoadingPending(false);
    }
  };

  const resendActivationEmail = async (adminId) => {
    setResendingEmail(adminId);
    try {
      const res = await axios.post(`${BACKEND_URL}/api/admin/resend-activation/${adminId}`, {
        base_url: window.location.origin
      }, { headers });
      
      if (res.data.email_sent) {
        toast.success('تم إعادة إرسال رابط التفعيل بنجاح');
      } else {
        toast.warning('لم يتم إرسال البريد - تحقق من الإعدادات');
      }
      fetchPendingAdmins();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'فشل في إعادة الإرسال');
    } finally {
      setResendingEmail(null);
    }
  };

  const handleSaveProfile = async () => {
    setSaving(true);
    try {
      const res = await axios.put(`${BACKEND_URL}/api/admin/profile`, profile, { headers });
      updateUser(res.data.user);
      toast.success('تم حفظ البيانات بنجاح');
    } catch (err) {
      toast.error('حدث خطأ أثناء الحفظ');
    } finally {
      setSaving(false);
    }
  };

  const handleChangePassword = async () => {
    if (passwords.new !== passwords.confirm) {
      toast.error('كلمة المرور الجديدة غير متطابقة');
      return;
    }
    if (passwords.new.length < 6) {
      toast.error('كلمة المرور يجب أن تكون 6 أحرف على الأقل');
      return;
    }
    setSavingPassword(true);
    try {
      await axios.put(`${BACKEND_URL}/api/admin/change-password`, {
        current_password: passwords.current,
        new_password: passwords.new
      }, { headers });
      toast.success('تم تغيير كلمة المرور بنجاح');
      setPasswords({ current: '', new: '', confirm: '' });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'حدث خطأ أثناء تغيير كلمة المرور');
    } finally {
      setSavingPassword(false);
    }
  };

  // إعادة تشفير رابط لوحة التحكم
  const handleRegenerateSecureUrl = async () => {
    setRegeneratingUrl(true);
    try {
      const response = await axios.post(`${BACKEND_URL}/api/admin/regenerate-secure-url`, {}, { headers });
      
      if (response.data.success) {
        setNewSecureUrl(response.data.new_url);
        setShowRegenerateModal(false);
        
        // إظهار رسالة نجاح مع الرابط الجديد
        toast.success(
          language === 'ar' 
            ? 'تم إعادة تشفير الرابط وإرسال الرابط الجديد للمشرفين' 
            : 'URL regenerated and sent to admins',
          { duration: 5000 }
        );
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || (language === 'ar' ? 'فشل في إعادة تشفير الرابط' : 'Failed to regenerate URL'));
      setShowRegenerateModal(false);
    } finally {
      setRegeneratingUrl(false);
    }
  };

  const handleCreateAdmin = async () => {
    // Validation
    if (!newAdmin.username.trim()) {
      toast.error('اسم المستخدم مطلوب');
      return;
    }
    if (!newAdmin.email.trim()) {
      toast.error('البريد الإلكتروني مطلوب');
      return;
    }
    if (!newAdmin.first_name.trim()) {
      toast.error('الاسم الأول مطلوب');
      return;
    }
    if (!newAdmin.last_name.trim()) {
      toast.error('الاسم الأخير مطلوب');
      return;
    }
    // المسمى الوظيفي إلزامي — هو الـ Role الذي تُبنى عليه الصلاحيات
    if (!newAdmin.job_title_id && !newAdmin.job_title_ar) {
      toast.error('المسمى الوظيفي إلزامي — كل موظف يجب أن يكون له مسمى (Role)');
      return;
    }

    setCreatingAdmin(true);
    setEmailSentInfo(null);
    try {
      const response = await axios.post(`${BACKEND_URL}/api/admin/create-admin`, {
        username: newAdmin.username,
        email: newAdmin.email,
        phone: newAdmin.phone,
        first_name: newAdmin.first_name,
        last_name: newAdmin.last_name,
        employee_id: newAdmin.employee_id || '',
        job_title_ar: newAdmin.job_title_ar || '',
        job_title: newAdmin.job_title_en || newAdmin.job_title || '',
        job_title_id: newAdmin.job_title_id || '',
        preferred_language: newAdmin.preferred_language || 'ar',
        base_url: window.location.origin
      }, { headers });
      
      const created = {
        username: newAdmin.username,
        email: newAdmin.email,
        emailSent: !!response.data.email_sent,
        passwordSetupLink: response.data.password_setup_link || null,
        adminLoginUrl: response.data.admin_login_url || null,
      };
      
      if (response.data.email_sent) {
        toast.success('تم إنشاء المشرف بنجاح وإرسال رابط تعيين كلمة المرور');
      } else {
        toast.warning('تم إنشاء المشرف ولكن فشل إرسال البريد');
      }
      
      // Show post-create confirmation dialog (replaces inline emailSentInfo)
      setSuccessDialog(created);
      fetchAllAdmins();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'حدث خطأ أثناء إنشاء المشرف');
    } finally {
      setCreatingAdmin(false);
    }
  };

  // Reset state to start a NEW admin creation flow
  const resetCreateAdminFlow = () => {
    setSuccessDialog(null);
    setEmailSentInfo(null);
    setSelectedSupervisor(null);
    setNewAdmin({
      username: '',
      email: '',
      phone: '',
      first_name: '',
      last_name: '',
      employee_id: '',
      job_title_ar: ''
    });
    fetchRegisteredSupervisors();
  };

  // Close the dialog and navigate back to admins hub
  const closeCreateAdminFlow = () => {
    setSuccessDialog(null);
    setActiveSection('admins');
    fetchAllAdmins();
  };

  if (!user) return null;

  if (sectionBlocked) return null;

  return (
    <div className="min-h-screen bg-[#030303]">
      {/* Background Effects */}
      <div className="fixed inset-0 pointer-events-none">
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[500px] bg-[#d4af37]/5 rounded-full blur-[150px]" />
      </div>

      {/* Unified Header */}
      <DashboardHeader 
        onBack={() => {
          // Handle back navigation
          if (activeSection === 'company-docs' && companyDocsBackRef.current && companyDocsBackRef.current()) {
            return; // handled inside وثائق الشركة (sub-view → back to hub)
          }
          if (activeSection === 'profile') {
            // Personal profile (إدارة الحساب) → back to Employee Dashboard
            navigate('/employee-dashboard');
          } else if (activeSection === 'main') {
            // 'main' is the admin hub — back goes to "ادمن" parent (Dashboard with asb-admin view)
            navigate('/dashboard?view=asb-admin');
          } else if (['create-admin', 'add-admin-id', 'all-admins', 'pending-admins', 'supervisor-records'].includes(activeSection)) {
            // sub-pages of "إدارة المشرفين" → back to admins hub
            setActiveSection('admins');
          } else if (activeSection === 'admins') {
            // إدارة المشرفين → back to "ادمن" (Dashboard with asb-admin view)
            navigate('/dashboard?view=asb-admin');
          } else if (['permissions', 'security', 'company-docs', 'partner-code', 'bank-account', 'expiry-watch'].includes(activeSection)) {
            // الصلاحيات/الأمان/وثائق الشركة/الإعدادات → back to "ادمن"
            navigate('/dashboard?view=asb-admin');
          } else {
            setActiveSection('main');
          }
        }}
      />

      {/* Breadcrumb - Unified pattern (max-w-7xl) */}
      <div className="border-b border-white/5" data-testid="settings-breadcrumb">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="py-3 text-sm flex items-center gap-2">
          <button onClick={() => navigate('/dashboard')} className="text-[#71717A] hover:text-[#d4af37] transition-colors cursor-pointer">
            {language === 'ar' ? 'لوحة التحكم' : 'Dashboard'}
          </button>
          <span className="text-[#71717A]">&lt;</span>

          {activeSection === 'profile' ? (
            <>
              <button onClick={() => navigate('/employee-dashboard')} className="text-[#71717A] hover:text-[#d4af37] transition-colors cursor-pointer">
                {language === 'ar' ? 'لوحة تحكم الموظف' : 'Employee Dashboard'}
              </button>
              <span className="text-[#71717A]">&lt;</span>
              <span className="text-[#d4af37]">{language === 'ar' ? 'إدارة الحساب' : 'Account Management'}</span>
            </>
          ) : (
            <>
              <button onClick={() => navigate('/dashboard?view=asb-admin')} className="text-[#71717A] hover:text-[#d4af37] transition-colors cursor-pointer">
                {language === 'ar' ? 'ASB606 ADMIN' : 'ASB606 ADMIN'}
              </button>
              <span className="text-[#71717A]">&lt;</span>
              {activeSection === 'admins' ? (
            <span className="text-[#d4af37]">{language === 'ar' ? 'إدارة المشرفين' : 'Admin Management'}</span>
          ) : ['create-admin', 'add-admin-id', 'all-admins', 'pending-admins', 'supervisor-records'].includes(activeSection) ? (
            <>
              <button onClick={() => setActiveSection('admins')} className="text-[#71717A] hover:text-[#d4af37] transition-colors cursor-pointer">
                {language === 'ar' ? 'إدارة المشرفين' : 'Admin Management'}
              </button>
              <span className="text-[#71717A]">&lt;</span>
              <span className="text-[#d4af37]">
                {activeSection === 'add-admin-id' ? (language === 'ar' ? 'إدراج مشرف جديد' : 'Register Supervisor') :
                 activeSection === 'all-admins' ? (language === 'ar' ? 'حسابات المشرفين والموظفين' : 'Supervisors & Staff Accounts') :
                 activeSection === 'supervisor-records' ? (language === 'ar' ? 'سجلات المشرفين والموظفين' : 'Supervisors & Staff Records') :
                 activeSection === 'pending-admins' ? (language === 'ar' ? 'متابعة الجديد' : 'Pending') :
                 activeSection === 'create-admin' ? (language === 'ar' ? 'إنشاء حساب يوزر' : 'Create Account') :
                 ''}
              </span>
            </>
          ) : activeSection === 'permissions' ? (
            <span className="text-[#d4af37]">{language === 'ar' ? 'صلاحيات المشرفين والأدمن' : 'Admin & Supervisor Permissions'}</span>
          ) : activeSection === 'security' ? (
            <span className="text-[#d4af37]">{language === 'ar' ? 'الأمان والحماية' : 'Security & Protection'}</span>
          ) : activeSection === 'company-docs' ? (
            <span className="text-[#d4af37]">{language === 'ar' ? 'وثائق الشركة' : 'Company Documents'}</span>
          ) : activeSection === 'partner-code' ? (
            <span className="text-[#d4af37]">{language === 'ar' ? 'كود الشريك' : 'Partner Code'}</span>
          ) : activeSection === 'bank-account' ? (
            <span className="text-[#d4af37]">{language === 'ar' ? 'الحسابات المالية للشركة' : 'Company Bank Accounts'}</span>
          ) : activeSection === 'expiry-watch' ? (
            <span className="text-[#d4af37]">{language === 'ar' ? 'مراقبة الصلاحيات' : 'Expiry Watch'}</span>
          ) : (
            <span className="text-[#d4af37]">{language === 'ar' ? 'إدارة المشرفين' : 'Admin Management'}</span>
          )}
            </>
          )}
          </div>
        </div>
      </div>

      {/* Main Content */}
      <main className="relative z-10 py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">

        {/* Security Section - الأمان والحماية */}
        {activeSection === 'security' && (
          <SecuritySection
            language={language}
            onRegenerateClick={() => setShowRegenerateModal(true)}
          />
        )}

        {/* Dashboard & Info Settings Section */}
        {/* Admins Management Section (إدارة حسابات المشرفين) */}
        {activeSection === 'admins' && (
          <AdminHubSection
            language={language}
            setActiveSection={setActiveSection}
            fetchSupervisorRecords={fetchSupervisorRecords}
            fetchAllAdmins={fetchAllAdmins}
            fetchPendingAdmins={fetchPendingAdmins}
          />
        )}

        {/* Add Admin via Emirates ID Section */}
        {activeSection === 'add-admin-id' && (
          <AddAdminIdSection
            language={language}
            jobTitles={jobTitles}
            headers={headers}
            onSuccess={() => { setActiveSection('admins'); fetchAllAdmins(); }}
          />
        )}

        {/* Create New Admin Section */}
        {activeSection === 'create-admin' && (
          <CreateAdminFromSupervisorSection
            language={language}
            emailSentInfo={emailSentInfo}
            loadingSupervisors={loadingSupervisors}
            registeredSupervisors={registeredSupervisors}
            setActiveSection={setActiveSection}
            selectedSupervisor={selectedSupervisor}
            handleSupervisorSelect={handleSupervisorSelect}
            newAdmin={newAdmin}
            setNewAdmin={setNewAdmin}
            creatingAdmin={creatingAdmin}
            handleCreateAdmin={handleCreateAdmin}
            successDialog={successDialog}
            onCreateAnother={resetCreateAdminFlow}
            onFinishCreate={closeCreateAdminFlow}
            jobTitles={jobTitles}
          />
        )}

        {/* All Admins Section */}
        {activeSection === 'all-admins' && (
          <AllAdminsSection
            language={language}
            allAdmins={allAdmins}
            loadingAdmins={loadingAdmins}
            sendAdminResetLink={sendAdminResetLink}
            toggleBanAdmin={toggleBanAdmin}
            deleteAdmin={archiveAdmin}
            jobTitles={jobTitles}
            refreshAdmins={fetchAllAdmins}
          />
        )}

        {/* قسم سجلات المشرفين والموظفين */}
        {activeSection === 'supervisor-records' && (
          <SupervisorRecordsSection
            language={language}
            supervisorRecords={supervisorRecords}
            loadingSupervisorRecords={loadingSupervisorRecords}
            supervisorRecordsSearch={supervisorRecordsSearch}
            setSupervisorRecordsSearch={setSupervisorRecordsSearch}
            fetchSupervisorRecords={fetchSupervisorRecords}
            onArchive={archiveSupervisor}
          />
        )}

        {/* قسم متابعة الجديد - Pending Admins */}
        {activeSection === 'pending-admins' && (
          <PendingAdminsSection
            language={language}
            loadingPending={loadingPending}
            pendingAdmins={pendingAdmins}
            fetchPendingAdmins={fetchPendingAdmins}
            resendActivationEmail={resendActivationEmail}
            resendingEmail={resendingEmail}
          />
        )}

        {/* قسم تشخيص توصيل البريد الإلكتروني */}
        {activeSection === 'email-diagnostics' && (
          <EmailDiagnosticsSection language={language} />
        )}

        {/* Profile Section - الملف الشخصي (الوصول من القائمة المنسدلة) */}
        {activeSection === 'profile' && (
          <ProfileSection
            language={language}
            profile={profile}
            setProfile={setProfile}
            passwords={passwords}
            setPasswords={setPasswords}
            showPasswords={showPasswords}
            setShowPasswords={setShowPasswords}
            saving={saving}
            savingPassword={savingPassword}
            onSaveProfile={handleSaveProfile}
            onChangePassword={handleChangePassword}
          />
        )}

        {/* Permissions Section - نظام الصلاحيات (Roles + تخصيص لموظف) */}
        {activeSection === 'permissions' && (
          <PermissionsSection
            language={language}
            loadingAdmins={loadingAdmins}
            allAdmins={allAdmins}
            headers={headers}
          />
        )}

        {/* Company Documents - وثائق الشركة */}
        {activeSection === 'company-docs' && (
          <CompanyDocumentsSection language={language} backRef={companyDocsBackRef} />
        )}

        {/* Partner Code + Bank Accounts - settings */}
        {activeSection === 'partner-code' && (
          <CompanySettingsSection language={language} mode="partner-code" />
        )}
        {activeSection === 'bank-account' && (
          <BankAccountsSection language={language} />
        )}
        {activeSection === 'expiry-watch' && (
          <ExpiryWatchSection language={language} />
        )}
        </div>
      </main>

      {/* Modal تأكيد إعادة التشفير */}
      {showRegenerateModal && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            className="bg-[#0A0A0A] border border-white/10 rounded-2xl p-6 max-w-md w-full"
          >
            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-red-500/20 flex items-center justify-center">
              <AlertTriangle className="h-8 w-8 text-red-400" />
            </div>
            <h3 className="text-xl font-bold text-white text-center mb-2">
              {language === 'ar' ? 'تأكيد إعادة التشفير' : 'Confirm Regeneration'}
            </h3>
            <p className="text-[#A1A1AA] text-center mb-6">
              {language === 'ar' 
                ? 'هل أنت متأكد من إعادة تشفير رابط لوحة التحكم؟ سيتم تسجيل خروجك وإرسال الرابط الجديد للمشرفين.'
                : 'Are you sure you want to regenerate the dashboard URL? You will be logged out and the new URL will be sent to admins.'}
            </p>
            <div className="flex gap-3">
              <Button
                onClick={() => setShowRegenerateModal(false)}
                disabled={regeneratingUrl}
                className="flex-1 bg-white/5 hover:bg-white/10 text-[#A1A1AA] border border-white/10"
              >
                {language === 'ar' ? 'إلغاء' : 'Cancel'}
              </Button>
              <Button
                onClick={handleRegenerateSecureUrl}
                disabled={regeneratingUrl}
                className="flex-1 bg-red-500 hover:bg-red-600 text-white"
              >
                {regeneratingUrl && <Loader2 className="h-4 w-4 animate-spin ml-2" />}
                {language === 'ar' ? 'تأكيد' : 'Confirm'}
              </Button>
            </div>
          </motion.div>
        </div>
      )}

      {/* Modal الرابط الجديد */}
      {newSecureUrl && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            className="bg-[#0A0A0A] border border-white/10 rounded-2xl p-6 max-w-md w-full"
          >
            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-emerald-500/20 flex items-center justify-center">
              <CheckCircle className="h-8 w-8 text-emerald-400" />
            </div>
            <h3 className="text-xl font-bold text-white text-center mb-2">
              {language === 'ar' ? 'تم إعادة التشفير بنجاح' : 'Successfully Regenerated'}
            </h3>
            <p className="text-[#A1A1AA] text-center mb-4">
              {language === 'ar' 
                ? 'تم إنشاء رابط جديد للوحة التحكم وإرساله للمشرفين عبر البريد الإلكتروني. يجب عليك تسجيل الدخول من الرابط الجديد.'
                : 'A new dashboard URL has been created and sent to admins via email. You need to login using the new URL.'}
            </p>
            <div className="bg-white/5 rounded-xl p-3 mb-4 overflow-hidden">
              <p className="text-emerald-400 text-sm font-mono break-all text-center" dir="ltr">
                {newSecureUrl}
              </p>
            </div>
            <Button
              onClick={() => {
                logout();
                window.location.href = newSecureUrl;
              }}
              className="w-full bg-emerald-500 hover:bg-emerald-600 text-white"
            >
              {language === 'ar' ? 'الذهاب للصفحة الجديدة' : 'Go to New Page'}
            </Button>
          </motion.div>
        </div>
      )}

      {/* Send Reset Link — language picker modal */}
      {resetLinkModal && (
        <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" data-testid="reset-link-modal" onClick={() => !resetLinkModal.sending && setResetLinkModal(null)}>
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }}
            onClick={(e) => e.stopPropagation()}
            className="bg-[#1a1a2e] border border-white/10 rounded-2xl max-w-md w-full p-6"
          >
            <h3 className="text-lg font-bold text-white mb-1">
              {language === 'ar' ? 'إرسال رابط إعادة تعيين كلمة المرور' : 'Send Password Reset Link'}
            </h3>
            <p className="text-sm text-[#A1A1AA] mb-4 break-all">
              {language === 'ar' ? 'إلى: ' : 'To: '}<span className="text-white">{resetLinkModal.admin?.email}</span>
            </p>

            <label className="block text-xs text-[#A1A1AA] mb-2">
              {language === 'ar' ? 'لغة البريد الإلكتروني' : 'Email language'}
            </label>
            <div className="grid grid-cols-2 gap-2 mb-2">
              {[
                { code: 'ar', label: '🇸🇦 العربية' },
                { code: 'en', label: '🇬🇧 English' },
              ].map((opt) => {
                const active = resetLinkModal.language === opt.code;
                return (
                  <button
                    key={opt.code}
                    type="button"
                    onClick={() => setResetLinkModal((m) => ({ ...m, language: opt.code }))}
                    disabled={resetLinkModal.sending}
                    data-testid={`reset-lang-${opt.code}`}
                    className={`h-11 rounded-xl border text-sm font-medium transition-all ${
                      active
                        ? 'bg-[#d4af37]/15 border-[#d4af37] text-[#d4af37]'
                        : 'bg-white/5 border-white/10 text-[#808080] hover:bg-white/10'
                    }`}
                  >
                    {opt.label}
                  </button>
                );
              })}
            </div>
            <p className="text-[10px] text-[#808080] mb-4">
              {(resetLinkModal.admin?.preferred_language || 'ar') === resetLinkModal.language
                ? (language === 'ar' ? '✓ هذه هي اللغة المفضلة للموظف' : '✓ This matches the employee\'s preferred language')
                : (language === 'ar' ? '⚠ غير اللغة الافتراضية للموظف' : '⚠ Different from the employee\'s default language')}
            </p>

            <div className="flex justify-end gap-2">
              <Button variant="outline" disabled={resetLinkModal.sending} onClick={() => setResetLinkModal(null)} className="border-white/10 text-white" data-testid="reset-modal-cancel">
                {language === 'ar' ? 'إلغاء' : 'Cancel'}
              </Button>
              <Button onClick={confirmSendResetLink} disabled={resetLinkModal.sending} className="bg-[#d4af37] hover:bg-[#c9a227] text-black" data-testid="reset-modal-send">
                {resetLinkModal.sending
                  ? (language === 'ar' ? 'جارٍ الإرسال...' : 'Sending...')
                  : (language === 'ar' ? 'إرسال' : 'Send')}
              </Button>
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
}
